
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import AutoComplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { Box } from '@mui/material';
import {
    baseUrl,
    ipAddress,
    userLocation,
    userName
} from '../Univ.js';

const DrugPayOther = () => {
    const { pat_id } = useParams();

    // =====================================================
    // STATE
    // =====================================================

    const [patientId, setPatientId] = useState('');
    const [patientName, setPatientName] = useState('');
    const [patientInfo, setPatientInfo] = useState({});

    const [consData, setConsData] = useState([]);
    const [selectedConsultant, setSelectedConsultant] = useState(null);

    const [departments, setDepartments] = useState([]);
    const [selectedDept, setSelectedDept] = useState('');
    const [departmentsLoaded, setDepartmentsLoaded] = useState(false);

    const [items, setItems] = useState([]);
    const [loadingStock, setLoadingStock] = useState(false);

    const [rows, setRows] = useState([
        {
            id: '',
            pharmacy_id: '',
            product: '',
            batch: '',
            expiry: '',
            totqty: '',
            uamount: '',
            qty: '',
            remainQty: '',
            netamount: ''
        }
    ]);

    const [subtotal, setSubtotal] = useState(0);
    const [total, setTotal] = useState(0);
    const [disPer, setDisPer] = useState(0);
    const [disAmount, setDisAmount] = useState(0);

    const [dbType, setDbType] = useState('cash');
    const [details, setDetails] = useState('');

    const [saving, setSaving] = useState(false);

    const inputRefs = useRef([]);
    const nextQtyInputRef = useRef(null);

    // =====================================================
    // FETCH PATIENT
    // =====================================================

    useEffect(() => {
        const fetchPatient = async () => {
            try {
                const response = await axios.get(
                    `${baseUrl}pat-id/${pat_id}/`
                );

                if (
                    response.status === 200 ||
                    response.status === 201
                ) {
                    const data = Array.isArray(response.data)
                        ? response.data[0] || {}
                        : response.data || {};

                    setPatientInfo(data);
                    setPatientId(data.id || '');
                    setPatientName(data.patient || '');
                }
            } catch (error) {
                console.error('Patient fetch error:', error);
                window.location.href = '/pat-search/';
            }
        };

        fetchPatient();
    }, [pat_id]);

    // =====================================================
    // FETCH CONSULTANTS
    // =====================================================

    useEffect(() => {
        const fetchConsultants = async () => {
            try {
                const response = await axios.get(
                    `${baseUrl}cons-search/`
                );

                setConsData(response.data || []);
            } catch (error) {
                console.error('Consultant fetch error:', error);
            }
        };

        fetchConsultants();
    }, []);

    const consultantOptions = consData.map(cons => ({
        value: cons.id,
        label: cons.consultant
    }));

    // =====================================================
    // FETCH DEPARTMENTS
    // =====================================================

    useEffect(() => {
        const fetchDepartments = async () => {
            try {
                const response = await axios.get(
                    `${baseUrl}pharmacy-departments/`
                );

                const list = response.data || [];

                setDepartments(list);

                const savedDept = sessionStorage.getItem('drugPayDept');

                const selected = list.find(dept =>
                    String(dept.id) === String(savedDept) ||
                    String(dept.department) === String(savedDept)
                ) || list[0];

                if (selected) {
                    const departmentName = String(selected.department);

                    setSelectedDept(departmentName);

                    sessionStorage.setItem(
                        'drugPayDept',
                        departmentName
                    );
                }
            } catch (error) {
                console.error('Department fetch error:', error);
                Swal.fire(
                    'Error',
                    'Unable to load pharmacy departments',
                    'error'
                );
            } finally {
                setDepartmentsLoaded(true);
            }
        };

        fetchDepartments();
    }, []);

    // =====================================================
    // FETCH STOCK WHEN DEPARTMENT CHANGES
    // =====================================================

    useEffect(() => {
        if (!departmentsLoaded || !selectedDept) {
            setItems([]);
            return;
        }

        let cancelled = false;

        const fetchStock = async () => {
            setLoadingStock(true);

            try {
                const response = await axios.get(
                    `${baseUrl}indent-pharm-search/`,
                    {
                        params: {
                            dept: selectedDept,
                            page: 1,
                            page_size: 2000
                        }
                    }
                );

                if (!cancelled) {
                    setItems(response.data || []);
                }
            } catch (error) {
                if (!cancelled) {
                    console.error('Stock fetch error:', error);
                    setItems([]);
                }
            } finally {
                if (!cancelled) {
                    setLoadingStock(false);
                }
            }
        };

        setItems([]);
        fetchStock();

        return () => {
            cancelled = true;
        };
    }, [selectedDept, departmentsLoaded]);

    // =====================================================
    // DEPARTMENT CHANGE
    // =====================================================

    const handleDeptChange = (e) => {
        const deptName = e.target.value;

        setSelectedDept(deptName);

        sessionStorage.setItem(
            'drugPayDept',
            deptName
        );

        // Clear previous department stock.
        setItems([]);

        // Clear previously selected medicines.
        setRows([
            {
                id: '',
                pharmacy_id: '',
                product: '',
                batch: '',
                expiry: '',
                totqty: '',
                uamount: '',
                qty: '',
                remainQty: '',
                netamount: ''
            }
        ]);
    };

    // =====================================================
    // CONSULTANT CHANGE
    // =====================================================

    const handleConsultantChange = (event, value) => {
        setSelectedConsultant(value);
    };

    // =====================================================
    // ROW MANAGEMENT
    // =====================================================

    const emptyRow = () => ({
        id: '',
        pharmacy_id: '',
        product: '',
        batch: '',
        expiry: '',
        totqty: '',
        uamount: '',
        qty: '',
        remainQty: '',
        netamount: ''
    });

    const handleAddRow = () => {
        setRows(prev => [
            ...prev,
            emptyRow()
        ]);
    };

    const handleDeleteRow = (index) => {
        setRows(prev => {
            const updated = prev.filter(
                (_, i) => i !== index
            );

            return updated.length > 0
                ? updated
                : [emptyRow()];
        });
    };

    const handleChange = (e, index) => {
        const { name, value } = e.target;

        setRows(prev =>
            prev.map((row, i) =>
                i === index
                    ? { ...row, [name]: value }
                    : row
            )
        );
    };

    // =====================================================
    // KEYBOARD HANDLING
    // =====================================================

    const handleKeyDown = (e, index) => {
        if (e.key === 'Shift') {
            e.preventDefault();
            handleAddRow();
            return;
        }

        if (e.key === 'Alt') {
            e.preventDefault();

            if (rows.length > 1) {
                handleDeleteRow(index);
            }

            return;
        }

        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();

            if (items.length === 0) return;

            setRows(prev => {
                const updated = [...prev];
                const current = updated[index];

                let selectedIndex = current.selectedItem || 0;

                if (e.key === 'ArrowUp') {
                    selectedIndex =
                        selectedIndex <= 0
                            ? items.length - 1
                            : selectedIndex - 1;
                } else {
                    selectedIndex =
                        selectedIndex >= items.length - 1
                            ? 0
                            : selectedIndex + 1;
                }

                updated[index] = {
                    ...current,
                    selectedItem: selectedIndex
                };

                return updated;
            });
        }
    };

    // =====================================================
    // MEDICINE SELECTION
    // =====================================================

    const handleSelect = (event, selectedItem, index) => {
        if (!selectedItem || !selectedItem.product) {
            return;
        }

        const expiryValue = selectedItem.product.expiry;

        if (expiryValue) {
            const expiryDate = new Date(
                `${expiryValue}T00:00:00`
            );

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const diffDays = Math.ceil(
                (expiryDate - today) / (1000 * 60 * 60 * 24)
            );

            if (diffDays <= 0) {
                Swal.fire(
                    'Expired Medicine',
                    `${selectedItem.product.product} has expired`,
                    'error'
                );
                return;
            }

            if (diffDays < 15) {
                Swal.fire(
                    'Expiry Warning',
                    `${diffDays} day(s) remaining. Total stock: ${selectedItem.qty}`,
                    'warning'
                );
            }
        }

        const existingIndex = rows.findIndex(
            row =>
                row.id &&
                String(row.id) === String(selectedItem.id)
        );

        if (existingIndex !== -1) {
            const updated = [...rows];
            const existing = updated[existingIndex];

            const newQty = Number(existing.qty || 0) + 1;
            const stockQty = Number(existing.totqty || 0);

            if (newQty > stockQty) {
                Swal.fire('Stock not available');
                return;
            }

            existing.qty = newQty;
            existing.remainQty = stockQty - newQty;
            existing.netamount =
                newQty * Number(existing.uamount || 0);

            setRows(updated);
            return;
        }

        const stockQty = Number(selectedItem.qty || 0);
        const price = Number(selectedItem.product.uamount || 0);

        const updated = [...rows];

        updated[index] = {
            id: selectedItem.id,
            pharmacy_id: selectedItem.product.id,
            dept: selectedDept,
            product: selectedItem.product.product,
            batch: selectedItem.product.batch,
            expiry: selectedItem.product.expiry,
            totqty: stockQty,
            uamount: price,
            qty: 1,
            remainQty: stockQty - 1,
            netamount: price
        };

        setRows(updated);

        setTimeout(() => {
            if (nextQtyInputRef.current) {
                nextQtyInputRef.current.focus();
            }
        }, 100);
    };

    // =====================================================
    // QUANTITY CHANGE
    // =====================================================

    const handleAmountChange = (e, index) => {
        const value = e.target.value;

        if (value.includes('.')) {
            return;
        }

        const qty = Number(value || 0);

        setRows(prev =>
            prev.map((row, i) => {
                if (i !== index) return row;

                const stockQty = Number(row.totqty || 0);
                const price = Number(row.uamount || 0);

                if (qty > stockQty) {
                    Swal.fire('Stock not available');

                    return {
                        ...row,
                        qty: stockQty,
                        remainQty: 0,
                        netamount: stockQty * price
                    };
                }

                if (qty < 0) {
                    return {
                        ...row,
                        qty: 0,
                        remainQty: stockQty,
                        netamount: 0
                    };
                }

                return {
                    ...row,
                    qty,
                    remainQty: stockQty - qty,
                    netamount: qty * price
                };
            })
        );
    };

    // =====================================================
    // DISCOUNT
    // =====================================================

    const handleDisPerChange = (e) => {
        let value = Number(e.target.value || 0);

        if (value < 0) value = 0;
        if (value > 100) value = 100;

        setDisPer(value);
    };

    useEffect(() => {
        const sub = rows.reduce(
            (sum, row) =>
                sum + Number(row.netamount || 0),
            0
        );

        const discount = sub * (Number(disPer || 0) / 100);
        const netTotal = sub - discount;

        setSubtotal(sub);
        setDisAmount(discount);
        setTotal(netTotal);
    }, [rows, disPer]);

    // =====================================================
    // FILTER AUTOCOMPLETE
    // =====================================================

    const customFilterOptions = (options, state) => {
        const inputValue = state.inputValue.toLowerCase();

        if (inputValue.length < 3) {
            return [];
        }

        return options.filter(option => {
            const productName =
                option.product?.product || '';

            return productName
                .toLowerCase()
                .startsWith(inputValue);
        });
    };

    // =====================================================
    // VALIDATE BILL DATA
    // =====================================================

    const getValidRows = () => {
        return rows.filter(
            row =>
                row.id &&
                row.pharmacy_id &&
                Number(row.qty) > 0
        );
    };

    const validateBill = () => {
        if (!selectedDept) {
            Swal.fire(
                'Error',
                'Please select a department',
                'error'
            );
            return false;
        }

        if (!selectedConsultant?.value) {
            Swal.fire(
                'Error',
                'Please select consultant',
                'error'
            );
            return false;
        }

        if (!patientName.trim()) {
            Swal.fire(
                'Error',
                'Please enter patient name',
                'error'
            );
            return false;
        }

        if (getValidRows().length === 0) {
            Swal.fire(
                'Error',
                'Please select medicines and enter quantity',
                'error'
            );
            return false;
        }

        return true;
    };

    // =====================================================
    // BUILD FORM DATA
    // =====================================================

    const buildFormData = (saleId, status) => {
        const validRows = getValidRows();

        const formData = new FormData();

        formData.append('sale_id', saleId || '');

        // Send department name instead of OtherBill.
        formData.append('sale_type', selectedDept);

        formData.append('pat', patientId);
        formData.append('patient', patientName);

        // Send consultant primary key.
        formData.append(
            'cons',
            selectedConsultant?.value || ''
        );

        // Keep IndentUse IDs as expected by existing billing API.
        formData.append(
            'product',
            validRows.map(row => row.id).join(',')
        );

        formData.append(
            'qty',
            validRows.map(row => row.qty).join(',')
        );

        formData.append(
            'netamount',
            validRows.map(row =>
                Number(row.netamount || 0).toFixed(2)
            ).join(',')
        );

        formData.append('total', total.toFixed(2));
        formData.append('disc', disAmount.toFixed(2));

        if (dbType === 'creditpay') {
            formData.append('paid', '0');
            formData.append('balance', total.toFixed(2));
        } else {
            formData.append('paid', total.toFixed(2));
            formData.append('balance', '0');
        }

        formData.append('mode', dbType);
        formData.append('details', details);
        formData.append('status', String(status));
        formData.append('addedby', userName);
        formData.append('ip_add', ipAddress);
        formData.append('location', userLocation);

        return formData;
    };

    // =====================================================
    // SAVE BILL
    // =====================================================

    const saveBill = async (saleId, status) => {
        const formData = buildFormData(saleId, status);

        const response = await axios.post(
            `${baseUrl}pharm-sale-other/`,
            formData
        );

        if (
            response.status !== 200 &&
            response.status !== 201
        ) {
            throw new Error('Bill save failed');
        }

        return response;
    };

    // =====================================================
    // PROCEED BILLING
    // =====================================================

    const proceedBilling = async () => {
        const validRows = getValidRows();

        // Generate sale number.
        const saleResponse = await axios.post(
            `${baseUrl}get_sale_pharm/`
        );

        const saleId = saleResponse.data.value;

        if (!saleId) {
            throw new Error('Unable to generate sale number');
        }

        // Update stock using IndentUse IDs.
        const updatePromises = validRows.map(row =>
            axios.post(
                `${baseUrl}update-indent-quantity/`,
                {
                    id: row.id,
                    qty: row.qty,
                    ip_add: ipAddress
                }
            )
        );

        await Promise.all(updatePromises);

        // Save bill after stock update.
        await saveBill(saleId, 1);

        Swal.fire({
            title: 'Pharmacy Bill Generated',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false
        });

        setTimeout(() => {
            window.location.href =
                '/pharm-invoice-other/' + saleId;
        }, 2000);
    };

    // =====================================================
    // SUBMIT BILL WITH DRUG ALERT VALIDATION
    // =====================================================

    const formSubmit = async (e) => {
        e.preventDefault();

        if (saving) return;
        if (!validateBill()) return;

        setSaving(true);

        try {
            const validRows = getValidRows();

            const drugIds = validRows.map(
                row => row.pharmacy_id
            );

            const qtyMap = {};

            validRows.forEach(row => {
                qtyMap[row.pharmacy_id] = row.qty;
            });

            const response = await axios.post(
                `${baseUrl}check-drug-alerts/`,
                {
                    drug_ids: drugIds,
                    patient_id: patientId,
                    qty_map: qtyMap
                }
            );

            const alerts = response.data.alerts || [];
            const stopSale = response.data.stop_sale || false;

            if (alerts.length > 0) {
                const html = alerts.map(alert => {
                    let color = '#333';

                    if (
                        alert.severity === 'Severe' ||
                        alert.severity === 'Contraindicated'
                    ) {
                        color = 'red';
                    } else if (
                        alert.severity === 'Moderate'
                    ) {
                        color = 'orange';
                    }

                    return `
                        <div style="
                            margin-bottom:10px;
                            padding:10px;
                            border:1px solid #ddd;
                            border-radius:5px;
                            text-align:left;
                        ">
                            <div style="
                                color:${color};
                                font-weight:bold;
                            ">
                                ${alert.type || ''}
                            </div>

                            <div>
                                ${alert.message || ''}
                            </div>

                            <div style="font-size:12px">
                                ${alert.description || ''}
                            </div>
                        </div>
                    `;
                }).join('');

                if (stopSale) {
                    await Swal.fire({
                        title: 'Prescription Blocked',
                        html,
                        icon: 'error',
                        width: 900
                    });

                    return;
                }

                const result = await Swal.fire({
                    title: 'Drug Alerts',
                    html,
                    icon: 'warning',
                    width: 900,
                    showCancelButton: true,
                    confirmButtonText: 'Proceed Anyway',
                    cancelButtonText: 'Cancel'
                });

                if (!result.isConfirmed) {
                    return;
                }
            }

            await proceedBilling();

        } catch (error) {
            console.error('Billing error:', error);

            Swal.fire(
                'Error',
                error.response?.data?.detail ||
                error.message ||
                'Failed to generate bill',
                'error'
            );
        } finally {
            setSaving(false);
        }
    };

    // =====================================================
    // SAVE AS DRAFT
    // =====================================================

    const formSubmitSave = async (e) => {
        e.preventDefault();

        if (saving) return;
        if (!validateBill()) return;

        setSaving(true);

        try {
            // Status 0 = saved bill/draft.
            // Stock is not updated here, preserving your old workflow.
            await saveBill('', 0);

            Swal.fire({
                title: 'Pharmacy Bill Saved',
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });

            setTimeout(() => {
                window.location.href = '/drug-view/';
            }, 2000);

        } catch (error) {
            console.error('Save bill error:', error);

            Swal.fire(
                'Error',
                error.response?.data?.detail ||
                error.message ||
                'Failed to save bill',
                'error'
            );
        } finally {
            setSaving(false);
        }
    };

    // =====================================================
    // RENDER
    // =====================================================

    return (
        <section className="container mt-4">

            {/* DEPARTMENT */}
            <div className="breadcrumb d-flex align-items-center gap-3">
                <select
                    className="form-select"
                    value={selectedDept}
                    onChange={handleDeptChange}
                    style={{ width: '200px' }}
                    disabled={!departmentsLoaded}
                >
                    <option value="">Select Department</option>

                    {departments.map(dept => (
                        <option
                            key={dept.id}
                            value={String(dept.department)}
                        >
                            {dept.department}
                        </option>
                    ))}
                </select>

                {loadingStock && (
                    <span className="text-primary">
                        Loading stock...
                    </span>
                )}
            </div>

            <form onSubmit={formSubmit}>

                {/* PATIENT DETAILS */}
                <div className="row table-responsive">
                    <div className="col-12 mb-2">

                        <table
                            className="erp-table-modern"
                            style={{
                                width: '100%',
                                tableLayout: 'fixed',
                                fontSize: '12px',
                                borderCollapse: 'collapse'
                            }}
                        >
                            <thead>
                                <tr style={{
                                    backgroundColor: '#1bc5ce',
                                    color: 'white',
                                    height: '32px'
                                }}>
                                    <th style={{ width: '10%', padding: '5px' }}>
                                        Patient ID
                                    </th>

                                    <th style={{ width: '20%', padding: '5px' }}>
                                        Patient Name
                                    </th>

                                    <th style={{ width: '8%', padding: '5px' }}>
                                        Gender
                                    </th>

                                    <th style={{ width: '12%', padding: '5px' }}>
                                        D.O.B/Age
                                    </th>

                                    <th style={{ width: '15%', padding: '5px' }}>
                                        Regd. On
                                    </th>

                                    <th style={{ width: '35%', padding: '5px' }}>
                                        Consultant
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                <tr>
                                    <td style={{ padding: '3px' }}>
                                        <input
                                            className="form-control form-control-sm"
                                            type="text"
                                            value={patientId}
                                            readOnly
                                            style={{
                                                fontSize: '12px',
                                                padding: '4px'
                                            }}
                                        />
                                    </td>

                                    <td style={{ padding: '3px' }}>
                                        <input
                                            className="form-control form-control-sm"
                                            type="text"
                                            value={patientName}
                                            onChange={e =>
                                                setPatientName(e.target.value)
                                            }
                                            style={{
                                                fontSize: '12px',
                                                padding: '4px'
                                            }}
                                        />
                                    </td>

                                    <td style={{ padding: '5px' }}>
                                        {patientInfo.gender || ''}
                                    </td>

                                    <td style={{ padding: '5px' }}>
                                        {patientInfo.age || ''}
                                    </td>

                                    <td style={{ padding: '5px' }}>
                                        {patientInfo.add_date || ''}
                                    </td>

                                    <td style={{ padding: '3px' }}>
                                        <AutoComplete
                                            size="small"
                                            options={consultantOptions}
                                            value={selectedConsultant}
                                            getOptionLabel={option =>
                                                option?.label || ''
                                            }
                                            isOptionEqualToValue={(
                                                option,
                                                value
                                            ) =>
                                                option.value === value.value
                                            }
                                            onChange={handleConsultantChange}
                                            renderInput={params => (
                                                <TextField
                                                    {...params}
                                                    required
                                                    size="small"
                                                    placeholder="Select Consultant"
                                                />
                                            )}
                                            sx={{
                                                width: '100%',
                                                '& .MuiInputBase-root': {
                                                    minHeight: '30px',
                                                    fontSize: '12px',
                                                    padding: '0px 4px'
                                                },
                                                '& .MuiInputBase-input': {
                                                    fontSize: '12px',
                                                    padding: '4px !important'
                                                }
                                            }}
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                    </div>
                </div>

                {/* MEDICINE TABLE */}
                <div className="table-responsive">

                    <table className="erp-table-modern">
                        <thead>
                            <tr style={{
                                backgroundColor: '#1bc5ce',
                                color: 'white'
                            }}>
                                <th style={{ width: 60 }}>ID</th>
                                <th style={{ width: 200 }}>Drug Name</th>
                                <th style={{ width: 70 }}>Qty</th>
                                <th style={{ width: 100 }}>Batch</th>
                                <th style={{ width: 90 }}>Expiry</th>
                                <th style={{ width: 90 }}>Qty in Stock</th>
                                <th style={{ width: 100 }}>MRP 1s</th>
                                <th style={{ width: 120 }}>Net Amount</th>
                                <th style={{ width: 80 }}>Remain Qty</th>
                                <th style={{ width: 80 }}>Action</th>
                            </tr>
                        </thead>

                        <tbody>
                            {rows.map((row, index) => (
                                <tr key={index}>

                                    <td>
                                        <input
                                            type="text"
                                            value={row.id}
                                            readOnly
                                            style={{ width: 60 }}
                                        />
                                    </td>
                                    <td>

                                    <AutoComplete
    id={`product-${index}`}
    sx={{ width: 200 }}
    componentsProps={{
        paper: {
            sx: {
                width: 800
            }
        }
    }}
    fullWidth
    options={items}
    value={
        items.find(
            item => item.product.product === row.product
        ) || null
    }
    getOptionLabel={(option) =>
        option.product.product || ''
    }
    onChange={(event, value, id) =>
        handleSelect(event, value, index, id)
    }
    renderInput={(params) => (
        <div ref={params.InputProps.ref}>
            <input
                {...params.inputProps}
                className="form-control"
                type="text"
                autoFocus
                onKeyDown={(e) =>
                    handleKeyDown(e, index)
                }
                style={{ background: "#F7EEFF" }}
            />
        </div>
    )}
    renderOption={(props, option) => (
        <Box
            component="li"
            {...props}
            sx={{
                width: 750,
                height: 50,
                borderRadius: 1
            }}
        >
            {option.product
                ? `${option.product.product} ID: ${option.product.id} Expy: (${option.product.expiry}) 1s MRP: (${option.product.uamount}) Batch: ${option.product.batch} TQty: ${option.qty}`
                : ''}
        </Box>
    )}
    filterOptions={customFilterOptions}
    ref={(el) => inputRefs.current[index] = el}
/>
                                    </td>

                                    <td>
                                        <input
                                            type="number"
                                            min="0"
                                            step="1"
                                            style={{ width: 70 }}
                                            value={row.qty}
                                            onChange={e =>
                                                handleAmountChange(e, index)
                                            }
                                            onKeyDown={e => {
                                                if (
                                                    e.key === '.' ||
                                                    e.key === 'e' ||
                                                    e.key === 'E'
                                                ) {
                                                    e.preventDefault();
                                                }

                                                handleKeyDown(e, index);
                                            }}
                                            ref={
                                                index === rows.length - 1
                                                    ? nextQtyInputRef
                                                    : null
                                            }
                                        />
                                    </td>

                                    <td>
                                        <input
                                            type="text"
                                            style={{ width: 100 }}
                                            value={row.batch}
                                            readOnly
                                        />
                                    </td>

                                    <td>
                                        <input
                                            type="text"
                                            style={{ width: 90 }}
                                            value={row.expiry}
                                            readOnly
                                        />
                                    </td>

                                    <td>
                                        <input
                                            type="number"
                                            style={{ width: 90 }}
                                            value={row.totqty}
                                            readOnly
                                        />
                                    </td>

                                    <td>
                                        <input
                                            type="number"
                                            style={{ width: 100 }}
                                            value={row.uamount}
                                            readOnly
                                        />
                                    </td>

                                    <td>
                                        <input
                                            type="number"
                                            style={{ width: 120 }}
                                            value={Number(
                                                row.netamount || 0
                                            ).toFixed(2)}
                                            readOnly
                                        />
                                    </td>

                                    <td>
                                        <input
                                            type="number"
                                            style={{ width: 80 }}
                                            value={row.remainQty}
                                            readOnly
                                        />
                                    </td>

                                    <td>
                                        <button
                                            type="button"
                                            className="btn btn-danger btn-sm"
                                            onClick={() =>
                                                handleDeleteRow(index)
                                            }
                                        >
                                            Delete
                                        </button>
                                    </td>

                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <button
                        type="button"
                        className="btn btn-success addmore mt-4"
                        onClick={handleAddRow}
                    >
                        Add More
                    </button>

                    {/* TOTALS AND PAYMENT */}
                    <div className="row ms-5 mt-5">

                        <div className="col-md-6">

                            <div
                                className="ms-5"
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                }}
                            >

                                <div className="form-group">
                                    <label style={{ fontWeight: 'bold' }}>
                                        Subtotal:
                                    </label>

                                    <div className="input-group">
                                        <div className="input-group-addon me-2">
                                            &#8377;
                                        </div>

                                        <input
                                            type="number"
                                            value={subtotal.toFixed(2)}
                                            readOnly
                                        />
                                    </div>
                                </div>

                                <div className="form-group mt-3">
                                    <label style={{ fontWeight: 'bold' }}>
                                        Discount:
                                    </label>

                                    <div className="input-group">
                                        <div className="input-group-addon me-2">
                                            %
                                        </div>

                                        <input
                                            type="number"
                                            min="0"
                                            max="100"
                                            value={disPer}
                                            onChange={handleDisPerChange}
                                        />
                                    </div>
                                </div>

                                <div className="form-group mt-3">
                                    <label style={{ fontWeight: 'bold' }}>
                                        Discount Amount:
                                    </label>

                                    <div className="input-group">
                                        <div className="input-group-addon me-2">
                                            &#8377;
                                        </div>

                                        <input
                                            type="number"
                                            value={disAmount.toFixed(2)}
                                            readOnly
                                        />
                                    </div>
                                </div>

                                <div className="form-group mt-3">
                                    <label style={{ fontWeight: 'bold' }}>
                                        Total:
                                    </label>

                                    <div className="input-group">
                                        <div className="input-group-addon me-2">
                                            &#8377;
                                        </div>

                                        <input
                                            type="number"
                                            value={total.toFixed(2)}
                                            readOnly
                                        />
                                    </div>
                                </div>

                            </div>
                        </div>

                        <div className="col-md-6">

                            <div className="form-group">
                                <label>Choose Payment Type</label>

                                <select
                                    className="form-select"
                                    value={dbType}
                                    onChange={e =>
                                        setDbType(e.target.value)
                                    }
                                >
                                    <option value="cash">Cash</option>
                                    <option value="card">Card</option>
                                    <option value="creditpay">
                                        Credit Payment
                                    </option>
                                    <option value="UPI">UPI</option>
                                </select>
                            </div>

                            <div className="form-group mt-3">
                                <label>Details:</label>

                                <textarea
                                    className="no-comma form-control"
                                    rows={2}
                                    cols={2}
                                    value={details}
                                    onChange={e =>
                                        setDetails(e.target.value)
                                    }
                                />
                            </div>

                        </div>
                    </div>

                    {/* ACTION BUTTONS */}
                    <div
                        className="mb-5"
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        <button
                            type="button"
                            onClick={formSubmit}
                            className="btn btn-primary mx-2"
                            disabled={saving}
                        >
                            {saving ? 'Processing...' : 'Submit'}
                        </button>

                        <button
                            type="button"
                            onClick={formSubmitSave}
                            className="btn btn-info mx-2"
                            disabled={saving}
                        >
                            {saving ? 'Processing...' : 'Save'}
                        </button>
                    </div>

                </div>
            </form>
        </section>
    );
};

export default DrugPayOther;
